<script lang="ts">
  import AuthorPanel from '$lib/components/website/AuthorPanel.svelte';
  import Badge from '$lib/components/control/Badge.svelte';
  import Breadcrumbs from '$lib/components/website/Breadcrumbs.svelte';
  import { makePanelOption } from '$lib/helpers/makePanelOption.js';
  import { dateFormatter } from '$lib/helpers/stringFormatters.js';
  import {
    CARD_PANEL_HEADING_ALIGNMENT,
    CARD_PANEL_VARIANT,
    CONTENT_TYPES,
  } from '$lib/constants/constants.js';
  import CardPanel from '$lib/components/website/CardPanel.svelte';
  import ShareButton from '$lib/components/website/ShareButton.svelte';
  import { ensureOptimizely, optimizelyStore } from '$lib/stores/optimizelyStore';
  import { onMount } from 'svelte';

  export let data;

  const hasVariationContainer = data?.pageData?.contentCollection.items.find(
    (panel) => panel?.contentType === CONTENT_TYPES.VARIATION_CONTAINER,
  );

  onMount(async () => {
    if (hasVariationContainer && !$optimizelyStore.isReady) {
      await ensureOptimizely();
    }
  });
</script>

<article class="w-full flex flex-col items-center">
  <section class="w-full flex flex-col gap-12 justify-center items-center py-16 lg:gap-16">
    <div
      class="flex flex-col justify-center items-center px-4 gap-8 w-full max-w-1280 lg:px-8 lg:gap-10"
    >
      <div class="flex flex-col justify-center items-center max-w-[66.25rem] text-center">
        <div>
          <Breadcrumbs
            links={[
              { label: 'Sweat', href: '/' },
              { label: 'Blogs', href: '/blogs/news' },
              {
                label: data?.pageData?.category,
                href: `/blogs/${data?.pageData?.category.toLowerCase()}`,
              },
              {
                label: data?.pageData?.title,
                href: `/blogs/${data?.pageData?.category.toLowerCase()}${data?.pageData?.slug.toLowerCase()}`,
                isCurrentUrl: true,
              },
            ]}
            currentTextColor="text-primary-500"
            textColor="text-primary-200"
            chevronColor="#FFCCDD"
          />
        </div>

        <h1 class="text-display-md text-gray-900 font-bold mt-3 lg:text-display-2xl">
          {data?.pageData?.title}
        </h1>
        <p class="text-lg text-gray-600 font-medium mt-4 lg:mt-6 lg:text-display-xxs">
          {data?.pageData?.articleSummary ?? ''}
        </p>
      </div>
      <AuthorPanel authorPanel={data?.pageData?.author} authorSummary={true}>
        <div slot="description">
          <p class="text-xs text-gray-600 font-normal lg:text-sm">
            <span>{dateFormatter(data?.pageData?.publishedDate)}</span>
            {#if data?.pageData?.dateLastUpdated}
              <span>- Updated {dateFormatter(data?.pageData?.dateLastUpdated)}</span>
            {/if}
          </p>
          {#if data?.pageData?.readingTime}
            <p class="text-xs text-gray-600 font-normal lg:text-sm">
              {data?.pageData?.readingTime}
            </p>
          {/if}
        </div>
      </AuthorPanel>
    </div>
    <div class="w-full h-[15rem] max-w-1024 sm:px-8 sm:h-[42.688rem]">
      <img
        src="{data?.pageData?.heroImage?.url}?w=1024&q=85"
        alt={data?.pageData?.heroImage?.description || data?.pageData?.heroImage?.title}
        class="w-full h-full object-cover sm:rounded-xl"
      />
    </div>
  </section>
  <div class="w-full justify-center items-center flex flex-col gap-8 pb-20 lg:gap-16">
    {#if !hasVariationContainer || (hasVariationContainer && $optimizelyStore.isReady)}
      {#each data?.pageData?.contentCollection.items as panel}
        <div class={makePanelOption(panel, 'articlePage').style}>
          <svelte:component
            this={makePanelOption(panel, 'articlePage').component}
            {...makePanelOption(panel, 'articlePage').props}
          />
        </div>
      {/each}
    {/if}
  </div>
  <div class="px-4 flex flex-col items-center w-full lg:pb-24">
    <div class="w-full max-w-800">
      <AuthorPanel authorPanel={data?.pageData?.author} />
      <div
        class="py-6 flex flex-col gap-6 lg:flex-row lg:gap-12 lg:justify-between lg:items-center"
      >
        {#if data?.pageData?.tags?.length > 0}
          <div class="flex flex-row gap-2 flex-wrap">
            {#each data?.pageData?.tags as tag, index}
              {#if index <= 4}
                <Badge text={tag} />
              {/if}
            {/each}
          </div>
        {/if}
        <div>
          <ShareButton />
        </div>
      </div>
      {#if data?.articleStandardDisclaimer?.content}
        <div class="py-8">
          <p class="text-xs text-gray-400 font-normal">
            {data?.articleStandardDisclaimer?.content}
          </p>
        </div>
      {/if}
      {#if data?.pageData?.additionalDisclaimer?.content}
        <div class="py-8">
          <p class="text-xs text-gray-400 font-normal">
            {data?.pageData?.additionalDisclaimer?.content}
          </p>
        </div>
      {/if}
    </div>
  </div>
  {#if data?.pageData?.relatedStoriesCollection.length > 0}
    <div class="bg-gray-25 w-full flex justify-center lg:pt-16">
      <div
        class="p-4 flex flex-col justify-center w-full max-w-[440px] sm:max-w-1280 lg:px-8 lg:py-0 lg:mb-24"
      >
        <CardPanel
          heading="Recommended Stories"
          subHeading={data?.pageData?.category}
          cardsCollection={data?.pageData?.relatedStoriesCollection}
          variant={CARD_PANEL_VARIANT.SLIDER}
          headingAlignment={CARD_PANEL_HEADING_ALIGNMENT.LEFT}
        />
      </div>
    </div>
  {/if}
</article>
